import { HttpClient } from '@angular/common/http';
import {
    DefaultDataService,
    DefaultDataServiceConfig,
    EntityHttpResourceUrls,
    HttpUrlGenerator,
    Logger, QueryParams
} from '@ngrx/data';
import { Observable } from 'rxjs/index';

/**
 * Simple configuration to define url's for an entity and how to transform the response
 */
export interface EntityDataServiceConfig<T> extends DefaultDataServiceConfig {
    entityName: string;
    entityResourceUrl?: string;
    collectionResourceUrl?: string;

    // Transform functions (e.g. map(response=>response.entities)
    getAllTransform?(response): Observable<Array<T>>;
    getByIdTransform?(response): Observable<T>;
    getWithQueryTransform?(response): Observable<Array<T>>;

    // TODO: Consider if we should provide transform operations for response from add, delete, update and insert?
    // TODO: What about specifications

    // Original function signatures from default-data.services
    // add(entity: T): Observable<T>;
    // delete(key: number | string): Observable<number | string>;
    // getAll(): Observable<T[]>;
    // getById(key: number | string): Observable<T>;
    // getWithQuery(queryParams: QueryParams | string): Observable<T[]>;
    // update(update: Update<T>): Observable<T>;
    // upsert(entity: T): Observable<T>;

}

/**
 * If an entity API does not follow the default (root not api/ and response is not directly an array of entities)
 * this provides a wrapper that takes a EntityDataServiceConfig and the wrapper object simply needs to be registered
 * with the EntityDataService for example in the module where the entity is defined and used.
 *
 * const heroDevicesDataServiceConfig: EntityDataServiceConfig<Hero> = {
 *     entityName: 'Hero',
 *     entityResourceUrl: 'api2/hero/',
 *     collectionResourceUrl: 'api2/heroes/',
 *     getAllTransform: map(response => (response as any).heroes)
 * };
 *
 * export class HeroModule {
 *     constructor(httpClient: HttpClient, httpUrlGenerator: HttpUrlGenerator, logger: Logger, entityDataService: EntityDataService) {
 *         entityDataService.registerService(heroDevicesDataServiceConfig.entityName,
 *             new EntityDataServiceWrapper(heroDevicesDataServiceConfig}, httpClient, httpUrlGenerator, logger));
 *     }
 * }
 */
export class EntityDataServiceWrapper<T> extends DefaultDataService<any> {
    private serviceConfig: EntityDataServiceConfig<T>;

    constructor(serviceConfig: EntityDataServiceConfig<T>,
                httpClient: HttpClient,
                httpUrlGenerator: HttpUrlGenerator,
                logger: Logger) {
        const entityHttpResourceUrls: EntityHttpResourceUrls = {
            [serviceConfig.entityName]: {
                entityResourceUrl: serviceConfig.entityResourceUrl,
                collectionResourceUrl: serviceConfig.collectionResourceUrl
            }
        };

        httpUrlGenerator.registerHttpResourceUrls(entityHttpResourceUrls);
        super(serviceConfig.entityName, httpClient, httpUrlGenerator);
        // Just making this private/public in the constructor signature requires calling super() as first expression
        this.serviceConfig = serviceConfig;
    }

    getAll(): Observable<Array<T>> {
        if (this.serviceConfig.getAllTransform !== undefined) {
            return super.getAll()
                .pipe(
                    this.serviceConfig.getAllTransform
                );
        }

        return super.getAll();
    }

    getWithQuery(params: QueryParams): Observable<Array<T>> {
        if (this.serviceConfig.getWithQueryTransform !== undefined) {
            return super.getWithQuery(params)
                .pipe(
                    this.serviceConfig.getWithQueryTransform
                );
        }

        return super.getWithQuery(params);
    }

    getById(key: number | string): Observable<T> {
        if (this.serviceConfig.getByIdTransform !== undefined) {
            return super.getById(key)
                .pipe(
                    this.serviceConfig.getByIdTransform
                );
        }

        return super.getById(key);
    }

}
